import { cn } from '@/lib/utils';
import type { CaptureConfidence, QualityCard } from '@/lib/api/types';

interface QualityCardPanelProps {
  card?: QualityCard;
  /** Shown alone when an older API returns no card. */
  confidence?: CaptureConfidence;
}

const GRADE_TONE: Record<QualityCard['grade'], string> = {
  A: 'border-success text-success',
  B: 'border-success text-success',
  C: 'border-warning text-warning',
  D: 'border-warning text-warning',
  F: 'border-danger text-danger',
};

/**
 * The graded quality card (INTERFACES.md 15.2 `QualityCard`): the grade, the
 * overall score, the one sentence naming the fix, and the weighted parts
 * behind it. Display only; nothing on this screen gates on it. On the
 * result screen it renders inside the sheet the grade chip opens.
 *
 * The verdict sits beside the grade rather than under the parts, because it
 * is the one line an operator standing at the item acts on.
 */
export function QualityCardPanel({ card, confidence }: QualityCardPanelProps) {
  if (!card) {
    if (!confidence) return null;
    return (
      <section aria-label="Quality" className="text-sm">
        <p className="text-muted">Confidence {Math.round(confidence.fused * 100)}%</p>
      </section>
    );
  }

  return (
    <section aria-label="Quality" className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'flex size-16 shrink-0 flex-col items-center justify-center rounded-control border-2',
            GRADE_TONE[card.grade],
          )}
        >
          <span className="text-2xl font-semibold leading-none">{card.grade}</span>
          <span className="mt-1 text-xs">{card.overall}%</span>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <h3 className="text-sm font-semibold">Data quality</h3>
          <p className="text-sm">{card.verdict}</p>
        </div>
      </div>

      <ul className="grid gap-x-4 gap-y-2 sm:grid-cols-2">
        {card.parts.map((part) => {
          const applies = part.weight > 0;
          return (
            <li key={part.name} className="flex flex-col gap-1 text-xs">
              <span className="flex items-baseline justify-between gap-2">
                <span className="text-muted">
                  {part.name}
                  {applies && <span className="ml-1">({Math.round(part.weight <= 1 ? part.weight * 100 : part.weight)}%)</span>}
                </span>
                <span className={cn('tabular-nums', applies && part.score < 50 && 'text-warning')}>
                  {applies ? part.score : 'n/a'}
                </span>
              </span>
              <span className="h-1.5 overflow-hidden rounded-full bg-white/10" aria-hidden="true">
                <span
                  className={cn('block h-full rounded-full', part.score < 50 ? 'bg-warning' : 'bg-accent')}
                  style={{ width: `${applies ? part.score : 0}%` }}
                />
              </span>
              {part.note && <span className="text-muted">{part.note}</span>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
